import { Roles, Feature, BackOfficeRoles } from 'types';

// ACCOUNTING OVERVIEW (ACCOUNTING-OVERVIEW-PLAN.md, approved §20). Every
// owner switch and drawing rule is one line here, mirrored from the backend's
// clix-shared/services/accountingOverview/definitions.js. No money is
// computed on this page: Ops sends every figure; these lines only choose how
// it is shown.

// --- Owner switches (§15) -----------------------------------------------------

/** Q10: the Export button (WP-05 defaulted it to removed; the V2 mockup
 * draws it). Pending the owner: on by default. */
export const EXPORT_ENABLED = true;

/** Custom ranges: at most 13 months (as Payroll). */
export const MAX_RANGE_MONTHS = 13;

// --- Who opens /accounting/overview (§5, Q14) ---------------------------------

/** Company Owner, Accounting and Administrative, each with the Accounting
 * feature. Every other Accounting role keeps today's Accounting. */
export const VIEWER_ROLES: readonly string[] = Object.freeze([
  Roles.COMPANY_OWNER,
  BackOfficeRoles.ACCOUNTING,
  BackOfficeRoles.ADMINISTRATIVE,
]);

export const REQUIRED_FEATURE = Feature.ACCOUNTING;

// --- Live data (§4.4 request budget, §6 states) -------------------------------

/** Summary and profit while the page is visible. */
export const SUMMARY_POLL_MS = 60 * 1000;
/** Profit while Ops says a compute is running: a stored-rows read of a few
 * ms (plan §25), and a batch of shows lands in about a second, so the
 * figures appear as soon as they exist (owner, 28 Oct: faster). */
export const PROFIT_REFRESHING_POLL_MS = 2 * 1000;
/** The 2 s re-checks stop after this long (~30 tries) and fall back to
 * SUMMARY_POLL_MS: a compute that takes longer is not progressing. */
export const PROFIT_REFRESHING_MAX_MS = 60 * 1000;
/** The payroll card (payroll's copies change slowly). */
export const PAYROLL_CARD_POLL_MS = 5 * 60 * 1000;

/** Why a figure shows "—" or the last snapshot, by Ops' state (the tile
 * chip; short so it fits a tile at every width) and, as its tooltip, the
 * whole reason. Ops has no "none" state: a missing snapshot is queued
 * (refreshing) or refused (selling, burst, disabled); after a failed or
 * slow run, or before Ops has its table, it is 'paused' a few minutes. */
export const STATE_TEXT = Object.freeze({
  burst: 'Database busy',
  disabled: 'Profit off',
  paused: 'Updating soon',
  tooLong: 'Range too long',
  selling: 'After selling hours',
});

export const STATE_TITLE = Object.freeze({
  selling: 'Updates after selling hours',
  disabled: 'Profit updates are switched off',
  burst: 'Updates when the database is less busy',
  paused: 'Profit updates resume in a few minutes',
  tooLong: 'Profit is shown for ranges of up to 31 days',
});

/** The export's 409 (refused while selling / low burst). */
export const EXPORT_REFUSED_TEXT =
  'The export runs outside selling hours. Try again later.';

// --- Folding rows (§3.1 #10, #12) ---------------------------------------------

/** Expenses by Category: Ops sends the top 4 and "Other" (read.js:253). */
export const CATEGORY_ROWS = 4;
/** Payment Methods: Ops sends at most 4 rows, the 4th "Other" (read.js:225). */
export const METHOD_ROWS = 4;
/** Chargebacks card rows (Ops sends at most this many). */
export const CHARGEBACK_ROWS = 3;
/** Profit Margin: the 3 shows with the highest booth gross. */
export const MARGIN_ROWS = 3;

// --- Drawing rules (the mockups' own) -----------------------------------------

/** Profit Margin row colours: at or above GOOD green, below LOW amber. */
export const MARGIN_GOOD = 60;
export const MARGIN_LOW = 45;

/** iPhone bars (Mobile - Accounting.dc.html #1a): the bar is the row's
 * whole-number share × this scale, at most 100 %. */
export const PHONE_BAR_SCALE = Object.freeze({
  margin: 2,
  methods: 2,
  categories: 2.4,
});

/** Revenue vs Expenses (desktop): the drawn 500 × 140 plot; the highest
 * value of either line sits at TOP, zero on the bottom edge. */
export const CHART = Object.freeze({ W: 500, H: 140, TOP: 18 });
/** iPhone bars: 96 px high with the labels, the tallest bar 78. */
export const PHONE_BAR_MAX = 78;

/** Week buckets start on Monday (plan §3.1 #9). */
export const WEEK_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
